<template>
  <div class="dispatch-page">
    <div class="dispatch-bg"></div>

    <header class="dispatch-header">
      <div>
        <p class="eyebrow">DERAS DISPATCH CENTER</p>
        <h1>我的派单邀请</h1>
        <p>服务者专用接单台：集中查看 DeRAS 推荐、倒计时、候选评分和处理结果。</p>
      </div>
      <div class="header-actions">
        <button class="ghost-btn" @click="$router.push({ name: 'home' })">返回首页</button>
        <button class="primary-btn" @click="fetchInvitations">刷新邀请</button>
      </div>
    </header>

    <section class="summary-grid">
      <article><span>待处理</span><strong>{{ summary.pending || 0 }}</strong></article>
      <article><span>已接受</span><strong>{{ summary.accepted || 0 }}</strong></article>
      <article><span>已拒绝</span><strong>{{ summary.rejected || 0 }}</strong></article>
      <article><span>已超时</span><strong>{{ summary.expired || 0 }}</strong></article>
    </section>

    <section class="filter-card">
      <el-radio-group v-model="status" size="small" @change="fetchInvitations">
        <el-radio-button label="">全部</el-radio-button>
        <el-radio-button label="PENDING">待处理</el-radio-button>
        <el-radio-button label="ACCEPTED">已接受</el-radio-button>
        <el-radio-button label="REJECTED">已拒绝</el-radio-button>
        <el-radio-button label="EXPIRED">已超时</el-radio-button>
        <el-radio-button label="CANCELLED">已取消</el-radio-button>
      </el-radio-group>
      <span>谁先接受谁锁单，其他候选会自动取消。</span>
    </section>

    <section v-loading="loading" class="invitation-list">
      <article
        v-for="item in invitations"
        :key="item.id"
        :class="['invite-card', statusClass(item)]"
      >
        <div class="invite-main">
          <div class="invite-top">
            <div>
              <span class="type-pill">{{ serviceTypeLabel(item.serviceType) }}</span>
              <span class="type-pill muted">{{ robotTypeLabel(item.robotCategory || item.expectedRobotType) }}</span>
            </div>
            <el-tag :type="statusTag(item.dispatchStatus)">{{ statusLabel(item.dispatchStatus) }}</el-tag>
          </div>

          <h2>{{ item.taskTitle || '未命名任务' }}</h2>
          <p class="address-line">{{ routeText(item) }}</p>

          <div class="metric-row">
            <div>
              <span>倒计时</span>
              <strong>{{ countdownText(item) }}</strong>
            </div>
            <div>
              <span>候选分</span>
              <strong>{{ formatScore(item.dispatchScore) }}</strong>
            </div>
            <div>
              <span>距离</span>
              <strong>{{ formatDistance(item.distanceMeters) }}</strong>
            </div>
            <div>
              <span>平台补助</span>
              <strong>{{ formatCoin(item.estimatedRobocoin) }}</strong>
            </div>
          </div>

          <div class="reason-box">
            <strong>DeRAS 推荐原因</strong>
            <p>{{ item.dispatchReason || item.scoreSummary || '类型匹配、SDK在线、机器人空闲、接单开关开启。' }}</p>
          </div>

          <div class="voucher-box">
            <div>
              <span>SCHEDULING VOUCHER</span>
              <strong>{{ item.schedulingVoucherNo || voucherNo(item) }}</strong>
              <p>DeRAS 离线调度凭证：记录任务、候选机器人、评分、排名、邀请时间和超时策略。</p>
            </div>
            <code>{{ shortDigest(item.schedulingVoucherDigest) }}</code>
          </div>

          <div class="score-tags">
            <small v-for="passed in item.passed || []" :key="`p-${item.id}-${passed}`">{{ passed }}</small>
          </div>
        </div>

        <aside class="robot-panel">
          <img v-if="item.robotCoverImageUrl" :src="item.robotCoverImageUrl" :alt="item.robotName" />
          <div v-else class="robot-placeholder">ROBOT</div>
          <strong>{{ item.robotName || '我的机器人' }}</strong>
          <span>{{ item.sdkAgentOnline ? 'SDK Agent 在线' : 'SDK Agent 离线' }}</span>
          <em>{{ heartbeatText(item) }}</em>
          <div class="invite-actions">
            <button
              class="primary-btn"
              :disabled="!canHandle(item)"
              @click="acceptInvite(item)"
            >
              {{ handlingId === item.id ? '处理中...' : '接受派单' }}
            </button>
            <button
              class="ghost-btn danger"
              :disabled="!canHandle(item)"
              @click="rejectInvite(item)"
            >
              拒绝
            </button>
          </div>
        </aside>
      </article>

      <el-empty v-if="!loading && invitations.length === 0" description="暂无派单邀请"></el-empty>
    </section>
  </div>
</template>

<script>
import {
  acceptDispatchInvitation,
  getDispatchInvitations,
  rejectDispatchInvitation
} from '../api/messages'
import { getAuthUser } from '../utils/auth'

export default {
  name: 'DispatchInvitations',
  data() {
    return {
      currentUser: getAuthUser(),
      loading: false,
      handlingId: null,
      status: '',
      nowTs: Date.now(),
      timer: null,
      invitations: [],
      summary: {},
      robotTypes: [
        { value: 'CLEANING', label: '清洁机器人' },
        { value: 'DELIVERY', label: '配送机器人' },
        { value: 'SHUTTLE', label: '接驳机器人' },
        { value: 'INSPECTION', label: '巡检机器人' }
      ]
    }
  },
  mounted() {
    this.fetchInvitations()
    this.timer = window.setInterval(() => {
      this.nowTs = Date.now()
    }, 1000)
  },
  beforeUnmount() {
    if (this.timer) window.clearInterval(this.timer)
  },
  methods: {
    async fetchInvitations() {
      this.currentUser = getAuthUser()
      if (!this.currentUser || !this.currentUser.id) {
        this.invitations = []
        return
      }
      this.loading = true
      try {
        const response = await getDispatchInvitations({
          userId: this.currentUser.id,
          status: this.status || undefined
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '派单邀请加载失败')
          return
        }
        const data = response.data.data || {}
        this.invitations = data.records || []
        this.summary = data.summary || {}
      } catch (error) {
        this.$message.error('派单邀请请求失败')
      } finally {
        this.loading = false
      }
    },
    canHandle(item) {
      return item &&
        item.dispatchStatus === 'PENDING' &&
        this.remainingSeconds(item) > 0 &&
        this.handlingId !== item.id
    },
    remainingSeconds(item) {
      const expiresAt = item && item.expiresAt ? new Date(item.expiresAt).getTime() : NaN
      if (!Number.isFinite(expiresAt)) return 0
      return Math.max(0, Math.floor((expiresAt - this.nowTs) / 1000))
    },
    countdownText(item) {
      if (item.dispatchStatus !== 'PENDING') return this.statusLabel(item.dispatchStatus)
      const seconds = this.remainingSeconds(item)
      if (seconds <= 0) return '已超时'
      const minutes = Math.floor(seconds / 60)
      const rest = seconds % 60
      return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
    },
    async acceptInvite(item) {
      if (!this.canHandle(item)) return
      try {
        await this.$confirm('确认接受这次 DeRAS 派单吗？接受后任务会立即锁定到你的机器人。', '接受派单', {
          confirmButtonText: '确认接受',
          cancelButtonText: '取消',
          type: 'warning'
        })
        this.handlingId = item.id
        const response = await acceptDispatchInvitation(item.id, { userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '接受派单失败')
          return
        }
        this.$message.success('已接受派单，任务进入进行中订单')
        await this.fetchInvitations()
        this.$router.push({ name: 'activeOrders' })
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('接受派单失败')
      } finally {
        this.handlingId = null
      }
    },
    async rejectInvite(item) {
      if (!this.canHandle(item)) return
      try {
        const result = await this.$prompt('请输入拒绝原因，DeRAS会继续尝试下一组候选机器人。', '拒绝派单', {
          confirmButtonText: '确认拒绝',
          cancelButtonText: '取消',
          inputValue: '当前机器人暂不方便执行该任务',
          inputValidator: value => Boolean(value && value.trim()),
          inputErrorMessage: '请填写拒绝原因'
        })
        this.handlingId = item.id
        const response = await rejectDispatchInvitation(item.id, {
          userId: this.currentUser.id,
          reason: result.value
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '拒绝派单失败')
          return
        }
        this.$message.success('已拒绝派单')
        await this.fetchInvitations()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('拒绝派单失败')
      } finally {
        this.handlingId = null
      }
    },
    routeText(item) {
      if (!item) return '-'
      if (item.serviceType === 'SHUTTLE') {
        return `${item.beginAddress || '起点未填写'} -> ${item.endAddress || '终点未填写'}`
      }
      return item.serviceAddress || '服务地址未填写'
    },
    heartbeatText(item) {
      const seconds = Number(item.sdkHeartbeatAgeSeconds)
      if (!Number.isFinite(seconds)) return '等待心跳'
      if (seconds < 60) return `${seconds}秒前心跳`
      return `${Math.floor(seconds / 60)}分钟前心跳`
    },
    formatDistance(value) {
      const meters = Number(value)
      if (!Number.isFinite(meters) || meters <= 0) return '未知'
      if (meters < 1000) return `${Math.round(meters)} m`
      return `${(meters / 1000).toFixed(2)} km`
    },
    formatScore(value) {
      const score = Number(value || 0)
      return Number.isFinite(score) ? score.toFixed(2) : '0.00'
    },
    voucherNo(item) {
      return item && item.taskId && item.id ? `SV-${item.taskId}-${item.id}` : 'SV-待生成'
    },
    shortDigest(value) {
      if (!value) return 'digest pending'
      const text = String(value)
      return text.length > 18 ? `${text.slice(0, 18)}...` : text
    },
    formatCoin(value) {
      const amount = Number(value || 0)
      return `${Number.isFinite(amount) ? amount.toFixed(2) : '0.00'} ROC`
    },
    statusLabel(value) {
      const map = {
        PENDING: '待确认',
        ACCEPTED: '已接受',
        REJECTED: '已拒绝',
        EXPIRED: '已超时',
        CANCELLED: '已取消'
      }
      return map[value] || value || '未知'
    },
    statusTag(value) {
      if (value === 'PENDING') return 'warning'
      if (value === 'ACCEPTED') return 'success'
      if (value === 'REJECTED' || value === 'EXPIRED') return 'danger'
      return 'info'
    },
    statusClass(item) {
      return String(item && item.dispatchStatus || '').toLowerCase()
    },
    robotTypeLabel(value) {
      const matched = this.robotTypes.find(item => item.value === value)
      return matched ? matched.label : (value || '未分类')
    },
    serviceTypeLabel(value) {
      return value === 'SHUTTLE' ? '接驳服务' : '到家服务'
    }
  }
}
</script>

<style scoped>
.dispatch-page {
  min-height: 100vh;
  position: relative;
  padding: 34px;
  color: #f5f7fb;
  background: linear-gradient(135deg, #07131f 0%, #101a24 54%, #07101a 100%);
}

.dispatch-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 44px 44px;
  opacity: 0.45;
}

.dispatch-header,
.summary-grid,
.filter-card,
.invitation-list {
  position: relative;
  z-index: 1;
}

.dispatch-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-end;
  margin-bottom: 22px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #85d8ff;
  font-size: 12px;
  letter-spacing: 0.28em;
}

.dispatch-header h1 {
  margin: 0;
  font-size: 46px;
}

.dispatch-header p {
  margin: 10px 0 0;
  color: rgba(245, 247, 251, 0.68);
}

.header-actions,
.invite-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.primary-btn,
.ghost-btn {
  border: none;
  border-radius: 999px;
  min-height: 38px;
  padding: 0 18px;
  cursor: pointer;
  font-weight: 800;
}

.primary-btn {
  background: linear-gradient(135deg, #52cfff, #79f0bd);
  color: #06111e;
}

.ghost-btn {
  background: rgba(255, 255, 255, 0.08);
  color: #eff6ff;
  border: 1px solid rgba(255, 255, 255, 0.16);
}

.ghost-btn.danger {
  color: #ffd2d2;
  border-color: rgba(255, 110, 110, 0.34);
}

.primary-btn:disabled,
.ghost-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 14px;
}

.summary-grid article,
.filter-card,
.invite-card {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(9, 20, 32, 0.78);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(18px);
}

.summary-grid article {
  border-radius: 8px;
  padding: 18px;
}

.summary-grid span,
.metric-row span {
  display: block;
  color: rgba(245, 247, 251, 0.6);
  font-size: 13px;
}

.summary-grid strong {
  display: block;
  margin-top: 8px;
  font-size: 30px;
}

.filter-card {
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.filter-card > span {
  color: rgba(245, 247, 251, 0.64);
}

.invitation-list {
  display: grid;
  gap: 16px;
}

.invite-card {
  border-radius: 8px;
  padding: 18px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 18px;
}

.invite-card.pending {
  border-color: rgba(255, 196, 87, 0.42);
}

.invite-card.accepted {
  border-color: rgba(105, 240, 174, 0.42);
}

.invite-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.type-pill {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(82, 207, 255, 0.14);
  color: #9fe5ff;
  margin-right: 8px;
  font-size: 12px;
  font-weight: 800;
}

.type-pill.muted {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(245, 247, 251, 0.72);
}

.invite-card h2 {
  margin: 14px 0 8px;
  font-size: 24px;
}

.address-line {
  margin: 0 0 14px;
  color: rgba(245, 247, 251, 0.68);
}

.metric-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.metric-row div {
  border-radius: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.06);
}

.metric-row strong {
  display: block;
  margin-top: 6px;
  color: #fff;
}

.reason-box {
  margin-top: 14px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(121, 240, 189, 0.08);
  border: 1px solid rgba(121, 240, 189, 0.16);
}

.reason-box p {
  margin: 6px 0 0;
  color: rgba(245, 247, 251, 0.72);
}

.voucher-box {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  margin-top: 12px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(119, 184, 255, 0.08);
  border: 1px solid rgba(119, 184, 255, 0.18);
}

.voucher-box span {
  display: block;
  margin-bottom: 5px;
  color: rgba(255, 211, 141, 0.88);
  font-size: 11px;
  font-weight: 900;
}

.voucher-box strong {
  display: block;
  color: #fff;
}

.voucher-box p {
  margin: 5px 0 0;
  color: rgba(245, 247, 251, 0.66);
  line-height: 1.5;
}

.voucher-box code {
  max-width: 180px;
  padding: 8px 10px;
  border-radius: 8px;
  color: #9bd7ff;
  background: rgba(7, 15, 27, 0.74);
  border: 1px solid rgba(119, 184, 255, 0.18);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.score-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.score-tags small {
  border-radius: 999px;
  padding: 5px 9px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(245, 247, 251, 0.68);
}

.robot-panel {
  border-radius: 8px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.055);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.robot-panel img,
.robot-placeholder {
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 8px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.08);
}

.robot-placeholder {
  display: grid;
  place-items: center;
  color: rgba(245, 247, 251, 0.56);
  font-weight: 900;
}

.robot-panel span,
.robot-panel em {
  color: rgba(245, 247, 251, 0.68);
  font-style: normal;
}

@media (max-width: 900px) {
  .dispatch-header,
  .filter-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .summary-grid,
  .metric-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .invite-card {
    grid-template-columns: 1fr;
  }
}
</style>
